import { Power, PowerOff } from 'lucide-react';
import Modal from './Modal';

interface ActivationConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string | React.ReactNode;
  workflowName?: string;
  isActivating: boolean; // true for activate, false for deactivate
  isLoading?: boolean;
}

export default function ActivationConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  workflowName,
  isActivating,
  isLoading = false,
}: ActivationConfirmationModalProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={onClose}
        disabled={isLoading}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleConfirm}
        disabled={isLoading}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${
          isActivating
            ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
            : 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
        }`}
      >
        {isLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            {isActivating ? 'Activating...' : 'Deactivating...'}
          </>
        ) : (
          <>
            {isActivating ? (
              <Power className="w-4 h-4" />
            ) : (
              <PowerOff className="w-4 h-4" />
            )}
            {isActivating ? 'Activate' : 'Deactivate'}
          </>
        )}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={footer}
      closeOnOverlayClick={!isLoading}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-4">
          <div
            className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
              isActivating
                ? 'bg-green-100'
                : 'bg-orange-100'
            }`}
          >
            {isActivating ? (
              <Power className="w-6 h-6 text-green-600" />
            ) : (
              <PowerOff className="w-6 h-6 text-orange-600" />
            )}
          </div>
          <div className="flex-1">
            <div className="text-gray-700">{description}</div>
            {workflowName && (
              <p className="mt-2 text-sm text-gray-600">
                <strong>"{workflowName}"</strong>
              </p>
            )}
          </div>
        </div>
        {isActivating && (
          <div className="border rounded-lg p-3 bg-green-50 border-green-200">
            <p className="text-sm text-green-800">
              <strong>Note:</strong> This will automatically assign the learning
              path to the specified group/role. Users will be notified based on
              the notification settings.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
